import request from "@/utils/request";
import { encryptPassword } from "@/utils/tools";
import url from "./url";

// 账户相关

/**
 * 登录API
 */
export const login = (username, password) => request({
    url: url.login,
    method: "post",
    data: {
        username,
        password: encryptPassword(password),
        oid: "webpage",
        mid: username,
    }
});

// 验证码相关

/**
 * 发送验证码API（注册）
 */
export const sendVerifyCodeForRegister = (phone, method = "SMS") => request({
    url: url.sendVerifyCodeForRegister,
    method: "post",
    data: { username: phone, verifymethod: method }
});

/**
 * 发送验证码API（忘记密码）
 */
export const sendForgotPasswordVerifyCode = (phone, method = "SMS") => request({
    url: url.sendForgotPasswordVerifyCode,
    method: "post",
    data: { username: phone, verifymethod: method }
});

// 注册相关

/**
 * 注册API
 * @param {Object} params
 * @param {string} params.phone      - 手机号
 * @param {string} params.password   - 密码（明文，内部加密）
 * @param {string} params.verifyCode - 短信验证码
 * @param {string} params.company    - 公司名（可选）
 * @param {string} params.inviteCode - 邀请码（可选）
 * @param {string} params.email      - 邮箱（可选）
 * @param {string} params.version    - 版本号（默认 1.0.0）
 */
export const register = ({ phone, password, verifyCode, company = "", inviteCode = "", email = "", version = "1.0.0" } = {}) => request({
    url: url.register,
    method: "post",
    data: {
        username: phone,
        verify_code: verifyCode,
        password: encryptPassword(password),
        company,
        incode: inviteCode,
        email,
        version,
    }
});

// 密码管理

/**
 * 忘记密码API
 */
export const forgotPassword = (phone, password, verifyCode) => request({
    url: url.forgotPassword,
    method: "post",
    data: {
        username: phone,
        password: encryptPassword(password),
        verify_code: verifyCode,
    }
});


/**
 * 邀请列表API
 * @param {Object} params
 * @param {number} params.page - 页码（默认1）
 * @param {number} params.size - 每页条数（默认10）
 */
export const invitelist = ({ page = 1, size = 10 } = {}) => request({
    url: url.invitelist,
    method: "post",
    data: { page, size }
});

/**
 * 修改密码API
 */
export const updatePassword = (oldPassword, newPassword) => request({
    url: url.updatePassword,
    method: "post",
    data: {
        password: encryptPassword(oldPassword),
        newpassword: encryptPassword(newPassword),
    }
});

// 用户信息管理

/**
 * 获取用户信息API
 */
export const getUserInfo = () => request({
    url: url.getUserInfo,
    method: "post",
    data: {}
});

/**
 * 更新用户信息API
 */
export const updateUserInfo = (company = "", email = "") => request({
    url: url.updateUserInfo,
    method: "post",
    data: { company, email }
});

// 商品

/**
 * 商品列表API
 */
export const getProductList = () => request({
    url: url.getProductList,
    method: "post",
    data: {}
});

// 订单相关

/**
 * 下单API
 */
export const createOrder = ({productId, discountList = [], price = 1, quantity = null}) => request({
    url: url.createOrder,
    method: "post",
    data: { product_id: productId, discount_list: discountList, price, quantity }
});

/**
 * 获取支付链接API（用于后续获取二维码）
 */
export const getPayLink = (orderId) => request({
    url: url.getPayLink,
    method: "post",
    data: { order_id: orderId }
});

/**
 * 查询订单状态API
 */
export const getOrderStatus = (orderId) => request({
    url: url.getOrderStatus,
    method: "post",
    data: { order_id: orderId }
});

/**
 * 获取订单详情API
 */
export const orderdetail = (data) => request({
    url: url.orderdetail,
    method: "post",
    data: data
});

/**
 * 取消订单API
 * @param {string} orderId - 订单ID
 */
export const cancelorder = (orderId) => request({
    url: url.cancelorder,
    method: "post",
    data: { order_id: orderId }
});

// 下载

/**
 * 获取下载信息API
 * @param {number} type - 产品ID，1:免费版，2:专业版，3:企业版
 */
export const getLatestVersion = (type = 1) => request({
    url: url.getLatestVersion,
    method: "post",
    data: { softwaretype: type }
});

// 优惠券 / 权益码

/**
 * 获取权益兑换记录API
 * @param {number} used - 使用状态，2:已使用
 * @param {number} type - 类型，1:权益码
 */
export const getCouponList = (used = 2, type = 1) => request({
    url: url.getCouponList,
    method: "post",
    data: { used, type }
});

/**
 * 领取优惠券API
 * @param {string} coupon_id   - 优惠券ID（可选）
 * @param {string} coupon_code - 优惠券代码（可选）
 * @param {number} type        - 1:领取不使用, 2:使用
 */
export const takeCoupon = (coupon_id = "", coupon_code = "", type) => {
    const data = { type };
    if (coupon_id)   data.coupon_id   = coupon_id;
    if (coupon_code) data.coupon_code = coupon_code;
    return request({ url: url.takeCoupon, method: "post", data });
};

// 授权管理

/**
 * 获取用户所有授权API
 * @returns {Promise} 返回授权列表
 */
export const getUserAuthList = () => request({
    url: url.getUserAuthList,
    method: "post",
    data: {}
});

/**
 * 剔除授权API
 * @param {string} mid - 设备ID
 * @returns {Promise} 返回操作结果
 */
export const dropUserAuth = (mid) => request({
    url: url.dropUserAuth,
    method: "post",
    data: { mid }
});


// 查询最近7天积分消耗信息
export const consumeSummary = ({days = 7}) => request({
    url: url.consumeSummary,
    method: "post",
    data: { days }
});

/**
 * 获取积分交易流水列表API
 * @param {Object} params
 * @param {number} params.transaction_type - 交易类型（可选）
 * @param {string} params.start_time     - 开始时间（可选）
 * @param {string} params.end_time       - 结束时间（可选）
 * @param {number} params.page           - 页码（可选，默认1）
 * @param {number} params.page_size      - 每页条数（可选，默认20）
 */
export const getTransactions = ({
    transaction_type,
    start_time,
    end_time,
    page = 1,
    page_size = 10,
} = {}) => {
    const data = { page, page_size };
    if (transaction_type !== undefined) data.transaction_type = transaction_type;
    if (start_time) data.start_time = start_time;
    if (end_time) data.end_time = end_time;
    return request({
        url: url.getTransactions,
        method: "post",
        data,
    });
};


export const exportTransactions = ({
    transaction_type,
    start_time,
    end_time,
} = {}) => {
    const data = {};
    if (transaction_type !== undefined) data.transaction_type = transaction_type;
    if (start_time) data.start_time = start_time;
    if (end_time) data.end_time = end_time;
    return request({
        url: url.exportTransactions,
        method: "post",
        data,
        isDownload: true,
    });
};

/**
 * 获取发票列表API
 */
export const orderlist = ({
    start_time,
    end_time,
    page = 1,
    limit = 10,
} = {}) => {
    const data = { page, limit };
    if (start_time) data.start_time = start_time;
    if (end_time) data.end_time = end_time;
    return request({
        url: url.orderlist,
        method: "post",
        data,
    });
};

// 查询当月积分消耗
export const getConsumeCurrentMonth = () => request({
    url: url.getConsumeCurrentMonth,
    method: "post",
});

// 获取产品 下次到期修改时间
export const nextRefreshPeriod = () => request({
    url: url.nextRefreshPeriod,
    method: "post",
});


// 登出方法
export const logout = () => request({
    url: url.logout,
    method: "post",
});


export const applyInvoice = (data) => request({
    url: url.applyInvoice,
    method: "post",
    data,
});

//  获取预签名 web重建的上传地址
export const presignedUploadUrl = (data) => request({
    url: url.presignedUploadUrl,
    method: "post",
    data,
});

/**
 * 创建高斯任务
 * @param {Object} data
 * @param {string} data.input_prefix - 3.1 返回的上传会话前缀
 * @param {string} data.task_name    - 任务名称
 * @param {string} data.cover        - 封面 file_key（必须在本次 input_prefix 下）
 */
export const createTask = (data) => request({
    url: url.createTask,
    method: "post",
    data,
});

/**
 * 查询任务状态（建议 3~5 秒轮询一次）
 * @param {string} taskId - 任务ID
 */
export const getTaskDetail = (taskId) => request({
    url: url.taskDetail,
    method: "post",
    data: { task_id: taskId },
});

/**
 * 获取预签名下载地址（CDN 鉴权地址）
 * @param {string} taskId  - 任务ID
 * @param {string} fileKey - 该任务的 output_file_key / preview_file_key / cover / extra_output_keys 之一
 */
export const presignedDownloadUrl = (taskId, fileKey) => request({
    url: url.presignedDownloadUrl.replace(":task_id", taskId),
    method: "post",
    data: { file_key: fileKey },
});

/**
 * 删除任务（queued 可删；processing 返回 40901；终态软删并清理云端资源）
 * @param {string} taskId - 任务ID
 */
export const deleteTask = (taskId) => request({
    url: url.deleteTask,
    method: "post",
    data: { task_id: taskId },
});

/**
 * 任务列表（按 createdate 倒序）
 * @param {Object} params
 * @param {number} params.limit  - 每页条数，默认 20，上限 100
 * @param {number} params.offset - 偏移量，默认 0
 */
export const getTask = ({ limit = 20, offset = 0 } = {}) => request({
    url: url.getTask,
    method: "post",
    data: { limit, offset },
});

/**
 * 修改任务名称（任何状态下都可改名，不影响任务状态与产物）
 * @param {string} taskId   - 任务ID
 * @param {string} taskName - 新名称，1~128 字符，不能为空串
 * @returns 更新后的完整任务对象（字段同 taskDetail），可直接替换列表里那一条
 */
export const renameTask = (taskId, taskName) => request({
    url: url.renameTask.replace(":task_id", taskId),
    method: "post",
    data: { task_name: taskName },
});

